import * as R from 'ramda';
import React, { memo } from 'react';
import Dropzone from 'react-dropzone';
// components
import { TextComponent } from '../text';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex } from '../../ui';
// component activities
import { renderConfirmationModal } from './helpers';
//////////////////////////////////////////////////

const whiteColor = G.getTheme('colors.white');
const blackColor = G.getTheme('colors.black');
const darkGreyColor = G.getTheme('colors.#7D828C');
const lightGreyColor = G.getTheme('colors.lightGrey');
const greyMatterhornColor = G.getTheme('colors.greyMatterhorn');

const wrapperStyles = {
  mb: 10,
  p: '7px',
  borderRadius: '4px',
  border: '1px solid',
  color: greyMatterhornColor,
  borderColor: lightGreyColor,
};

// TODO: check how to open csv and xlsx files

const ImagePreview = (props) => {
  const { openModal, document, onClickPreview, isPreviewAllowed } = props;

  const { filePublicLink, documentFilename, thumbnailFilePublicLink } = document;

  const title = `${G.getWindowLocale('actions:click-to-view', 'Click To View')} ${documentFilename}`;

  const wrapperStyles = {
    mr: 15,
    width: 50,
    height: 45,
    flexShrink: 0,
    justifyContent: 'center',
  };

  const cursor = G.ifElse(isPreviewAllowed, 'pointer', 'default');

  if (R.and(G.isNilOrEmpty(thumbnailFilePublicLink), G.isNotNilAndNotEmpty(filePublicLink))) {
    return (
      <Flex
        {...wrapperStyles}
        title={title}
        cursor={cursor}
        onClick={() => {
          if (R.not(isPreviewAllowed)) return;

          if (G.isFunction(onClickPreview)) return onClickPreview(document);

          G.openFileByPublicLink(filePublicLink);
        }}
      >
        {G.getDocumentIconByDocumentFileName(documentFilename, 30, 30)}
      </Flex>
    );
  }

  if (G.isNilOrEmpty(thumbnailFilePublicLink)) {
    return (
      <Flex {...wrapperStyles}>
        {G.getDocumentIconByDocumentFileName(documentFilename, 30, 30)}
      </Flex>
    );
  }

  const fileNotAvailable = G.getWindowLocale('messages:file-not-available', 'The file is not available');

  const handleOpenPreview = () => {
    const component = (
      <Box height='90vh'>
        {
          filePublicLink ?
            <img
              width='100%'
              height='100%'
              src={filePublicLink}
              alt={fileNotAvailable}
              style={{ objectFit: 'contain' }}
            /> :
            fileNotAvailable
        }
      </Box>
    );

    const modal = {
      p: '0px',
      component,
      options: {
        minWidth: 500,
        height: '90vh',
        withCloseIcon: true,
        maxWidth: 'calc(100vw - 50px)',
      },
    };

    openModal(modal);
  };

  return (
    <Flex {...wrapperStyles} cursor='pointer'>
      <img
        height={45}
        width='100%'
        title={title}
        alt={fileNotAvailable}
        src={thumbnailFilePublicLink}
        style={{ objectFit: 'contain' }}
        onClick={() => {
          if (G.isFunction(onClickPreview)) return onClickPreview(document);

          handleOpenPreview();
        }}
      />
    </Flex>
  );
};

const Document = memo((props) => {
  const {
    document,
    openModal,
    closeModal,
    showPreview,
    withThumbnail,
    isReadOnlyMode,
    onClickPreview,
    handleDocuments,
    handleDownloadOrPreviewDocument,
  } = props;

  const { guid, createdBy, createdDate, documentFilename } = document;

  const fileExt = R.last(R.split('.', documentFilename || ''));

  const isPreviewAllowed = R.and(
    G.isNotNilAndNotEmpty(documentFilename),
    R.includes(fileExt, GC.DOCUMENTS_EXTENSIONS),
  );

  const formattedCreatedDate = G.convertDateTimeToConfigFormat(createdDate);

  return (
    <Flex {...wrapperStyles} fontSize={12}>
      {
        G.isTrue(withThumbnail) &&
        <ImagePreview
          document={document}
          openModal={openModal}
          onClickPreview={onClickPreview}
          isPreviewAllowed={isPreviewAllowed}
        />
      }
      <Box width={`calc(100% - ${G.ifElse(withThumbnail, 65, 0)}px)`}>
        <Flex justifyContent='space-between'>
          <TextComponent withEllipsis={true} title={documentFilename} maxWidth='calc(100% - 75px)'>
            {documentFilename}
          </TextComponent>
          <Flex>
            {
              R.and(
                isPreviewAllowed,
                R.or(G.isNilOrFalse(withThumbnail), G.isTrue(showPreview)),
              ) &&
              <Box
                mr={10}
                cursor='pointer'
                onClick={() => {
                  if (G.isFunction(onClickPreview)) return onClickPreview(document);

                  handleDownloadOrPreviewDocument(document);
                }}
              >
                {I.eye(darkGreyColor)}
              </Box>
            }
            <Box
              mr={10}
              cursor='pointer'
              onClick={() => handleDownloadOrPreviewDocument({ ...document, actionType: 'download' })}
            >
              {I.downloadDocument(darkGreyColor)}
            </Box>
            {
              R.not(isReadOnlyMode) &&
              <Box
                cursor='pointer'
                onClick={() =>
                  renderConfirmationModal({ openModal, closeModal, action: () => handleDocuments('delete', {}, guid) })
                }
              >
                {I.trash(darkGreyColor)}
              </Box>
            }
          </Flex>
        </Flex>
        <Flex mt='5px'>
          <TextComponent
            mr='5px'
            title={createdBy}
            withEllipsis={true}
            color={darkGreyColor}
            maxWidth='calc(60% - 45px)'
          >
            {createdBy}
          </TextComponent>
          <TextComponent
            withEllipsis={true}
            color={darkGreyColor}
            maxWidth='calc(40% - 45px)'
            title={formattedCreatedDate}
          >
            {formattedCreatedDate}
          </TextComponent>
        </Flex>
      </Box>
    </Flex>
  );
});

const AddDocument = ({ handleDocuments }) => (
  <Dropzone
    className='drop-zone'
    onDrop={(files) => handleDocuments(
      'post',
      { data: G.makeDataForMultipleDocuments({ [GC.FIELD_FILES]: files || [] }) },
    )}
  >
    {({ getRootProps, getInputProps }) => (
      <Flex
        {...getRootProps()}
        m={15}
        px={15}
        height={60}
        bg={whiteColor}
        cursor='pointer'
        borderRadius='4px'
        textAlign='center'
        border='1px dashed'
        wordBreak='break-word'
        justifyContent='center'
        width='calc(100% - 30px)'
        color={G.getTheme('colors.dark.blue')}
        borderColor={G.getTheme('listActions.newBorderColor')}
      >
        <input {...getInputProps()} />
        Drag and Drop or select files from your Computer
      </Flex>
    )}
  </Dropzone>
);

const AddDocument2 = ({ m, onDropHandler }) => (
  <Dropzone
    className='drop-zone'
    onDrop={onDropHandler}
  >
    {({ getRootProps, getInputProps }) => (
      <Flex
        {...getRootProps()}
        px={15}
        m={m || 15}
        height={100}
        bg={whiteColor}
        cursor='pointer'
        borderRadius='4px'
        textAlign='center'
        border='1px dashed'
        flexDirection='column'
        wordBreak='break-word'
        justifyContent='center'
        width='calc(100% - 30px)'
        color={G.getTheme('colors.dark.blue')}
        borderColor={G.getTheme('listActions.newBorderColor')}
      >
        <input {...getInputProps()} />
        <Box mb={10}>{I.upload()}</Box>
        <Box mb={10}>Drag & Drop to Upload</Box>
        <Box textDecoration='underline'>or Select Files</Box>
      </Flex>
    )}
  </Dropzone>
);

const tabsProps = {
  textColor: 'inherit',
  scrollButtons: 'auto',
  indicatorColor: blackColor,
  style: { minHeight: '26px', borderBottom: `1px solid ${lightGreyColor}` },
  TabIndicatorProps: {
    sx: {
      height: 3,
      backgroundColor: blackColor,
    },
  },
};

const toolbarSettings = {
  link: { inDropdown: true },
  list: { inDropdown: true },
  inline: { inDropdown: true },
  history: { inDropdown: true },
  textAlign: { inDropdown: true },
  options: [
    'inline',
    'blockType',
    'fontSize',
    'fontFamily',
    'link',
    'emoji',
    'history',
    'list',
    'textAlign',
    'colorPicker',
    'remove',
  ],
};

export {
  Document,
  tabsProps,
  AddDocument,
  AddDocument2,
  toolbarSettings,
};
